import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getServiceRequestPage } from "services/get-serviceRequestPage";
import { useSetRecoilState, useRecoilState } from "recoil";
import { overlayState, toastState } from "atoms";
import { postServiceRequestPage } from "services/post-serviceRequestPage";
import { ResponseObject } from "atoms/models";
import "components/ServiceRequest/serviceRequest.scss";
import { PATH } from "utils/const";

interface FormErrorModel {
  customerNameFirstError: boolean;
  customerNameMiddleError: boolean;
  customerNameLastError: boolean;
  serviceStreetAddressError: boolean;
  serviceCityError: boolean;
  serviceStateError: boolean;
  serviceZipCodeError: boolean;
  phoneNumberError: boolean;
  emailAddressError: boolean;
  desiredServiceError: boolean;
  additionalCommentsError: boolean;
  newConstructionError: boolean;
  mobileHomeError: boolean;
  otherHousingTypeError: boolean;
  housingTypeError: boolean;
  desiredInstallationDateError: boolean;
}

interface FormValuesModel {
  customerNameFirst: string;
  customerNameMiddle: string;
  customerNameLast: string;
  serviceStreetAddress: string;
  serviceCity: string;
  serviceState: string;
  serviceZipCode: string;
  phoneNumber: string;
  emailAddress: string;
  desiredService: string;
  additionalComments: string;
  newConstruction: boolean;
  mobileHome: boolean;
  otherHousingType: boolean;
  housingType: boolean;
  desiredInstallationDate: string;
}

function ServiceRequestPage() {
  const setShowOverlay = useSetRecoilState(overlayState);
  const [toast, setToast] = useRecoilState(toastState);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const locationId: string | null = searchParams.get("locationId");
  const reason: string | null = searchParams.get("reason");
  const [formError, setFormError] = useState<FormErrorModel>({
    customerNameFirstError: false,
    customerNameMiddleError: false,
    customerNameLastError: false,
    serviceStreetAddressError: false,
    serviceCityError: false,
    serviceStateError: false,
    serviceZipCodeError: false,
    phoneNumberError: false,
    emailAddressError: false,
    desiredServiceError: false,
    additionalCommentsError: false,
    newConstructionError: false,
    mobileHomeError: false,
    otherHousingTypeError: false,
    housingTypeError: false,
    desiredInstallationDateError: false,
  });
  const [formValues, setFormValues] = useState<FormValuesModel>({
    customerNameFirst: "",
    customerNameMiddle: "",
    customerNameLast: "",
    serviceStreetAddress: "",
    serviceCity: "",
    serviceState: "",
    serviceZipCode: "",
    phoneNumber: "",
    emailAddress: "",
    desiredService: "",
    additionalComments: "",
    newConstruction: false,
    mobileHome: false,
    otherHousingType: false,
    housingType: false,
    desiredInstallationDate: "",
  });

  // Fetch address page data on component mount
  useEffect(() => {
    const fetchServiceAddressPage = async () => {
      setShowOverlay(true);
      const response: ResponseObject = await getServiceRequestPage(locationId, reason);
      if (response?._embedded["ec:address"]) {
        const { city, state, streetAddress, zipCode } = response._embedded["ec:address"];
        setFormValues({
          ...formValues,
          serviceCity: city,
          serviceStreetAddress: streetAddress,
          serviceState: state,
          serviceZipCode: zipCode,
        });
      }
      setShowOverlay(false);
    };
    fetchServiceAddressPage();
    // eslint-disable-next-line
  }, [locationId, reason, setShowOverlay]);

  useEffect(() => {
    window.scroll(0, 0);
  });

  // Validation function for email field
  const emailFieldValidation = (emailAddress: string) => {
    return emailAddress && emailAddress.includes("@") && emailAddress.includes(".");
  };

  // Handle form submission
  const handleSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formValues.customerNameFirst) {
      window.scrollTo(0, window.innerWidth < 576 ? 150 : 0);
      setFormError({ ...formError, customerNameFirstError: true });
    } else if (!formValues.customerNameLast) {
      window.scrollTo(0, window.innerWidth < 576 ? 150 : 0);
      setFormError({ ...formError, customerNameLastError: true });
    } else if (!formValues.serviceStreetAddress) {
      window.scrollTo(0, window.innerWidth < 576 ? 380 : 0);
      setFormError({ ...formError, serviceStreetAddressError: true });
    } else if (!formValues.serviceCity) {
      window.scrollTo(0, window.innerWidth < 576 ? 380 : 0);
      setFormError({ ...formError, serviceCityError: true });
    } else if (!formValues.serviceState) {
      window.scrollTo(0, window.innerWidth < 576 ? 380 : 0);
      setFormError({ ...formError, serviceStateError: true });
    } else if (!formValues.serviceZipCode) {
      window.scrollTo(0, window.innerWidth < 576 ? 380 : 0);
      setFormError({ ...formError, serviceZipCodeError: true });
    } else if (!formValues.phoneNumber) {
      window.scrollTo(0, window.innerWidth < 576 ? 630 : 0);
      setFormError({ ...formError, phoneNumberError: true });
    } else if (!emailFieldValidation(formValues.emailAddress)) {
      window.scrollTo(0, window.innerWidth < 576 ? 700 : 0);
      setFormError({ ...formError, emailAddressError: true });
    } else if (!formValues.desiredService || formValues.desiredService === "Select Desired Service") {
      window.scrollTo(0, window.innerWidth < 576 ? 700 : 0);
      setFormError({ ...formError, desiredServiceError: true });
    } else if (!formValues.newConstruction && !formValues.mobileHome && !formValues.otherHousingType) {
      window.scrollTo(0, window.innerWidth < 576 ? 900 : 0);
      setFormError({ ...formError, housingTypeError: true });
    } else if (!formValues.desiredInstallationDate) {
      window.scrollTo(0, window.innerWidth < 576 ? 900 : 0);
      setFormError({ ...formError, desiredInstallationDateError: true });
    } else {
      setShowOverlay(true);
      const response: ResponseObject = await postServiceRequestPage(formValues);
      navigate(PATH.SERVICE_REQUEST_CONF);
      setShowOverlay(false);
      if (!response) {
        setToast({
          ...toast,
          show: true,
          variant: "danger",
          header: "Error",
          message: "There was an error processing your request. Please check the information and try again.",
        });
      }
    }
  };

  // Format phone number in (xxx) xxx-xxxx format
  const formatPhoneNumber = (phoneNumber: string) => {
    let text = phoneNumber;
    text = text.replace(/\D/g, "");
    if (text.length > 3) text = text.replace(/.{3}/, "$&-");
    if (text.length > 7) text = text.replace(/.{7}/, "$&-");
    phoneNumber = text;

    return phoneNumber;
  };

  // Calculate minimum date for desired installation date input
  const blockoutPastDates = () => {
    const date = new Date();

    date.setDate(date.getDate() + 10);

    if (date.getDay() === 6) {
      date.setDate(date.getDate() + 2);
    } else if (date.getDay() === 0) {
      date.setDate(date.getDate() + 1);
    }

    const year = date.getFullYear();
    const getMonth = date.getMonth() + 1;
    const month = getMonth.toString().length === 1 ? `0${getMonth}` : getMonth;
    const getDay = date.getDate();
    const day = getDay.toString().length === 1 ? `0${getDay}` : getDay;

    return `${year}-${month}-${day}`;
  };

  return (
    <div className="service-request-container container">
      <div>
        <div className="row">
          <h3>
            The address you entered requires additional review prior to installation of service. Please fill out the form below to start the review
            process which can take 10-14 days.
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="request-form-group">
              <div className="request-input-label medium-width">
                <label className={formError.customerNameFirstError ? "control-label form-error-text form-error" : "control-label"}>First Name</label>
                <input
                  type="text"
                  className={formError.customerNameFirstError ? "form-control input-lg form-error" : "form-control input-lg"}
                  onFocus={() => setFormError({ ...formError, customerNameFirstError: false })}
                  value={formValues.customerNameFirst}
                  onChange={(e) => setFormValues({ ...formValues, customerNameFirst: e.target.value })}
                />
              </div>
              <div className="request-input-label">
                <label className="control-label">Middle</label>
                <input
                  type="text"
                  className="form-control input-lg"
                  value={formValues.customerNameMiddle}
                  onChange={(e) => setFormValues({ ...formValues, customerNameMiddle: e.target.value })}
                />
              </div>
              <div className="request-input-label medium-width">
                <label className={formError.customerNameLastError ? "control-label form-error-text" : "control-label"}>Last Name</label>
                <input
                  type="text"
                  className={formError.customerNameLastError ? "form-control input-lg form-error" : "form-control input-lg"}
                  onFocus={() => setFormError({ ...formError, customerNameLastError: false })}
                  value={formValues.customerNameLast}
                  onChange={(e) => setFormValues({ ...formValues, customerNameLast: e.target.value })}
                />
              </div>
            </div>
            <div className="request-form-group">
              <div className="request-input-label medium-width">
                <label className={formError.serviceStreetAddressError ? "control-label form-error-text" : "control-label"}>Address</label>
                <input
                  type="text"
                  className={formError.serviceStreetAddressError ? "form-control input-lg form-error" : "form-control input-lg"}
                  onFocus={() => setFormError({ ...formError, serviceStreetAddressError: false })}
                  value={formValues.serviceStreetAddress}
                  onChange={(e) => setFormValues({ ...formValues, serviceStreetAddress: e.target.value })}
                />
              </div>
              <div className="request-input-label medium-width">
                <label className={formError.serviceCityError ? "control-label form-error-text" : "control-label"}>City</label>
                <input
                  type="text"
                  className={formError.serviceCityError ? "form-control input-lg form-error" : "form-control input-lg"}
                  onFocus={() => setFormError({ ...formError, serviceCityError: false })}
                  value={formValues.serviceCity}
                  onChange={(e) => setFormValues({ ...formValues, serviceCity: e.target.value })}
                />
              </div>
              <div className="request-input-label small-width">
                <label className={formError.serviceStateError ? "control-label form-error-text" : "control-label"}>State</label>
                <input
                  type="text"
                  className={formError.serviceStateError ? "form-control input-lg form-error" : "form-control input-lg"}
                  onFocus={() => setFormError({ ...formError, serviceStateError: false })}
                  value={formValues.serviceState}
                  onChange={(e) => setFormValues({ ...formValues, serviceState: e.target.value })}
                />
              </div>
              <div className="request-input-label small-width">
                <label className={formError.serviceZipCodeError ? "control-label form-error-text" : "control-label"}>Zip Code</label>
                <input
                  type="text"
                  className={formError.serviceZipCodeError ? "form-control input-lg form-error" : "form-control input-lg"}
                  onFocus={() => setFormError({ ...formError, serviceZipCodeError: false })}
                  value={formValues.serviceZipCode}
                  onChange={(e) => setFormValues({ ...formValues, serviceZipCode: e.target.value })}
                />
              </div>
            </div>
            <div className="request-form-group">
              <div className="request-input-label medium-width">
                <label className={formError.phoneNumberError ? "control-label form-error-text" : "control-label"}>Phone Number</label>
                <input
                  type="tel"
                  className={formError.phoneNumberError ? "form-control input-lg form-error" : "form-control input-lg"}
                  onFocus={() => setFormError({ ...formError, phoneNumberError: false })}
                  maxLength={12}
                  value={formValues.phoneNumber}
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      phoneNumber: formatPhoneNumber(e.target.value),
                    })
                  }
                />
              </div>
              <div className="request-input-label medium-width">
                <label className={formError.emailAddressError ? "control-label form-error-text" : "control-label"}>Email</label>
                <input
                  type="emailAddress"
                  className={formError.emailAddressError ? "form-control input-lg form-error" : "form-control input-lg"}
                  onFocus={() => setFormError({ ...formError, emailAddressError: false })}
                  value={formValues.emailAddress}
                  onChange={(e) => setFormValues({ ...formValues, emailAddress: e.target.value })}
                />
              </div>
            </div>
            <div className="request-form-group">
              <div className="request-input-label small-width">
                <label className={formError.desiredServiceError ? "control-label form-error-text" : "control-label"}>Desired Service</label>
                <select
                  className={formError.desiredServiceError ? "form-control input-lg form-error" : "form-control input-lg"}
                  onFocus={() => setFormError({ ...formError, desiredServiceError: false })}
                  value={formValues.desiredService}
                  onChange={(e) => {
                    setFormValues({ ...formValues, desiredService: e.target.value });
                  }}
                >
                  <option style={{ color: "#555555" }}>Select Desired Service</option>
                  <option value={"internet"}>Internet</option>
                  <option value={"phoneNumber"}>Telephone</option>
                  <option value={"internetAndTelephone"}>Internet And Telephone</option>
                </select>
              </div>
              <div className="request-input-label full-width">
                <label className="control-label">Additional Comments</label>
                <input
                  type="text"
                  className="form-control input-lg"
                  onChange={(e) => setFormValues({ ...formValues, additionalComments: e.target.value })}
                />
              </div>
            </div>
            <div className="request-form-group" style={{ marginBottom: "-30px" }}>
              <p className={formError.housingTypeError ? "form-error-text" : ""}>
                <strong>Please select all that apply:</strong>
              </p>
            </div>
            <div className="request-form-group">
              <div className="request-input-label medium-width">
                <div className={formError.housingTypeError ? "checkbox-container housing-type-error" : "checkbox-container"}>
                  <label className="checkbox-inline me-2">
                    <input
                      className="me-1"
                      type="checkbox"
                      onFocus={() => setFormError({ ...formError, housingTypeError: false })}
                      checked={formValues.newConstruction}
                      onChange={(e) => {
                        setFormValues({
                          ...formValues,
                          newConstruction: e.target.checked,
                        });
                      }}
                    />
                    New Construction
                  </label>
                  <label className="checkbox-inline me-2">
                    <input
                      className="me-1"
                      type="checkbox"
                      onFocus={() => setFormError({ ...formError, housingTypeError: false })}
                      checked={formValues.mobileHome}
                      onChange={(e) => {
                        setFormValues({ ...formValues, mobileHome: e.target.checked });
                      }}
                    />
                    Mobile Home
                  </label>
                  <label className="checkbox-inline">
                    <input
                      className="me-1"
                      type="checkbox"
                      onFocus={() => setFormError({ ...formError, otherHousingTypeError: false })}
                      checked={formValues.otherHousingType}
                      onChange={(e) => {
                        setFormValues({
                          ...formValues,
                          otherHousingType: e.target.checked,
                        });
                      }}
                    />
                    Other
                  </label>
                </div>
              </div>
              <div className="request-input-label medium-width desired-install-date">
                <label className={formError.desiredInstallationDateError ? "control-label form-error-text" : "control-label"}>
                  {formError.desiredInstallationDateError ? "You must select a desired installation date" : "Desired Installation Date"}
                </label>
                <input
                  type="date"
                  className={formError.desiredInstallationDateError ? "form-control input-lg form-error" : "form-control input-lg"}
                  onFocus={() => setFormError({ ...formError, desiredInstallationDateError: false })}
                  min={blockoutPastDates()}
                  onChange={(e) => {
                    setFormValues({
                      ...formValues,
                      desiredInstallationDate: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
            <div className="request-form-group submit-button">
              <button type="submit" className="btn btn-primary">
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ServiceRequestPage;
